import { Component, OnInit, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DioService } from '../../../shared/services/dio.service';
import { GetServedServicePointResponse, GetServedServicePointRequest, UpdatePackageCountsRequest, GetDioPackageTypesResponse, PostPackageCountRequest, SetReceiverRequest } from '../../../shared/models/dio.models';
import { QuaggaJSConfigObject, QuaggaJSResultObject } from '@ericblade/quagga2';
import { BarcodeScannerLivestreamComponent } from 'ngx-barcode-scanner';
import { SignatureComponent } from '@syncfusion/ej2-angular-inputs';

@Component({
  templateUrl: 'visited-service-point.component.html',
  styleUrls: ['./visited-service-point.component.scss']
})
export class VisitedServicePointComponent implements OnInit, AfterViewInit {
  @ViewChild(BarcodeScannerLivestreamComponent) scanner!: BarcodeScannerLivestreamComponent;
  @ViewChild('scanner', { read: ElementRef }) scannerElement!: ElementRef;
  @ViewChild('signature') public signaturePad!: SignatureComponent;
  servedServicePointsResponse: GetServedServicePointResponse | null = null;
  selectedModel: GetServedServicePointResponse | null = null;
  packageCountModel: UpdatePackageCountsRequest | null = null;
  dioPackageTypes: GetDioPackageTypesResponse | null = null;
  packageEditFormPopupVisible: boolean = false;
  acceptPopupVisible: boolean = false;
  rejectPopupVisible: boolean = false;
  finishedVisitedServicePoint: boolean = false;
  private pendingPopupVisibilityUpdate: boolean = false;
  signatureHtmlSource: string = '';
  showScanBarcode: boolean = false;
  id: number;
  isPopupVisible = false;
  isMobile: boolean = false;
  isCameraFullScreen = false;
  removeItemBtnEnabled: boolean = false;
  selectedItem: string = '';
  cameraZoom: number = 1;
  ShowScanBarcode = false;
  scanfirstime = false;
  private isScannerInitialized = false;
  barcode: string = '';
  scanCodes: { code: string }[] = [];
  selectedItems: { code: string }[] = [];
  loading: boolean = true;

  config: Partial<QuaggaJSConfigObject> = {
    inputStream: {
      type: 'LiveStream',
      constraints: {
        width: 640,
        height: 480,
        facingMode: 'environment'
      }
    },
    decoder: {
      readers: ['ean_reader']
    },
    locate: true,
    locator: {
      patchSize: 'medium',
      halfSample: true
    }
  };

  constructor(
    private dioService: DioService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.id = this.route.snapshot.params['id'];
  }

  ngOnInit() {
    this.checkIfMobile();
    try {
      this.reloadServedServicePoint();
    } catch (error) {
      console.error('Error loading VisitedServicePointComponent:', error);
      this.router.navigate(['/voucher']);
    }
  }

  ngAfterViewInit() {
    if (this.acceptPopupVisible) {
      this.initializeSignaturePad();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkIfMobile();
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }

  reloadServedServicePoint() {
    const request: GetServedServicePointRequest = { id: this.id };
    this.dioService.getServedServicePoint(request).subscribe(response => {
      this.servedServicePointsResponse = response;
      this.finishedVisitedServicePoint = !!response.receiverName || !!response.rejectReason;
      if (response.receiverSignature) {
        this.signatureHtmlSource = `data:image/png;base64,${response.receiverSignature}`;
      }
    });
    this.dioService.getDioPackageTypes({}).subscribe(response => {
      this.dioPackageTypes = response;
      this.loading = false;
    });
  }

  navigateToServicePointManager() {
    this.router.navigate(['/service-point-manager']);
  }

  displayPackageType(item: any): string {
    return item ? `${item.packageTypeName} (${item.dioCode})` : '';
  }

  editPackageCounts(id: number) {
    this.packageCountModel = null;
  
    const model = this.servedServicePointsResponse?.packagesList.find(x => x.id === id);
    if (model) {
      this.packageCountModel = {
        id: model.id ?? 0,
        dioPackageTypeId: model.packageTypeId ?? 0,
        packageCount: model.packageCount ?? 0,
        scannedNumberCounts: model.scannedNumberCounts ?? 0,
        manualCount: model.manualCount ?? 0,
        scannedNumbers: model.scannedNumbers ?? ''
      };
      this.scanCodes = this.packageCountModel.scannedNumbers.split(';').map(code => ({ code }));
  
      this.packageEditFormPopupVisible = true;
    } else {
      console.warn(`Pack with ID: ${id} not found.`);
    }
  }
  
  addPackageCounts() {
    this.packageCountModel = {
      id: 0,
      dioPackageTypeId: 0,
      packageCount: 0,
      scannedNumberCounts: 0,
      manualCount: 0,
      scannedNumbers: '',
      dioServedServicePointId: this.id
    } as UpdatePackageCountsRequest;
  
    this.scanCodes = []; 
    this.packageEditFormPopupVisible = true;
  }
  
  acceptUpdatePackageCount() {
    if (this.packageCountModel) {
      this.packageEditFormPopupVisible = false;
      if (this.packageCountModel.id === 0) {
        const postRequest: PostPackageCountRequest = {
          dioServedServicePointId: this.id,
          dioPackageTypeId: this.packageCountModel.dioPackageTypeId,
          packageCount: this.packageCountModel.packageCount ?? 0,
          scannedNumbers: this.packageCountModel.scannedNumbers ?? '',
          scannedNumberCounts: this.packageCountModel.scannedNumberCounts ?? 0,
          manualCount: this.packageCountModel.manualCount ?? 0
        };
        this.dioService.postPackageCount(postRequest).subscribe(() => {
          this.reloadServedServicePoint();
          this.resetPopupState();
        });
      } else {
        this.dioService.updatePackageCounts(this.packageCountModel).subscribe(() => {
          this.reloadServedServicePoint();
          this.resetPopupState();
        });
      }
    } else {
      console.warn('No pack data to save.');
    }
  }
  
  cancelUpdatePackageCount() {
    this.resetPackageEditPopupState();
  }

  private resetPopupState() {
    this.packageCountModel = null;
    this.packageEditFormPopupVisible = false;
  }

  acceptBtnClick() {
    if (this.servedServicePointsResponse) {
      this.servedServicePointsResponse.receiverSignature = '';
      this.acceptPopupVisible = true;
    }
  }

  async acceptFormOkBtnClick() {
    if (this.servedServicePointsResponse) {
      this.acceptPopupVisible = false;
  
      const signatureDataUrl = this.signaturePad.getSignature(); 
  
      if (!signatureDataUrl || typeof signatureDataUrl !== 'string') {
        console.error('Signature pad is empty or not initialized');
        return;
      }
  
      const signatureBase64 = signatureDataUrl.split(',')[1]; 
  
      const request: SetReceiverRequest = {
        id: this.id,
        receiverName: this.servedServicePointsResponse.receiverName,
        receiverSignature: signatureBase64
      };
  
      try {
        const response = await this.dioService.setReceiver(request).toPromise();
  
  
        this.navigateToServicePointManager();
      } catch (error) {
        console.error('Error setting receiver:', error);
        throw new Error('Nav paraksts');
      }
    }
  }

  acceptFormCancelBtnClick() {
    if (this.servedServicePointsResponse) {
      this.servedServicePointsResponse.receiverName = '';
      this.acceptPopupVisible = false;
    }
  }

  rejectBtnClick() {
    this.rejectPopupVisible = true;
  }

  rejectFormOkBtnClick() {
    if (this.servedServicePointsResponse) {
      this.rejectPopupVisible = false;
      this.dioService.setRejectReason({
        id: this.id,
        rejectReason: this.servedServicePointsResponse.rejectReason ?? ''
      }).subscribe(() => this.navigateToServicePointManager());
    }
  }

  rejectFormCancelBtnClick() {
    if (this.servedServicePointsResponse) {
      this.servedServicePointsResponse.rejectReason = '';
      this.rejectPopupVisible = false;
    }
  }

  removeUpdatePackageCount() {
    if (this.packageCountModel && this.packageCountModel.id !== 0) {
      this.packageEditFormPopupVisible = false;
  
      this.dioService.deletePackageCount({ id: this.packageCountModel.id }).subscribe(() => {
        this.reloadServedServicePoint();
        this.resetPackageEditPopupState();
      });
    } else {
      console.warn('No pack selected for deletion.');
    }
  }

  backBtnClick() {
    this.router.navigate(['/service-point-manager']);
  }

  processScanCodes() {
    if (this.packageCountModel) {
      this.packageCountModel.scannedNumbers = this.scanCodes.map(item => item.code).join(';');
      this.packageCountModel.scannedNumberCounts = this.scanCodes.length;
      this.packageCountModel.packageCount = (this.packageCountModel.scannedNumberCounts ?? 0) + (this.packageCountModel.manualCount ?? 0);
    }
  }

  onManualCountChanged(newValue: number) {
    if (this.packageCountModel) {
      this.packageCountModel.manualCount = newValue;
      this.packageCountModel.packageCount = (this.packageCountModel.scannedNumberCounts ?? 0) + (this.packageCountModel.manualCount ?? 0);
    }
  }

  onSelectedItemsChanged(event: any) {
    this.selectedItems = event.selectedRowsData;
    this.removeItemBtnEnabled = this.selectedItems.length > 0;
  }

  removeItemBtnClick() {
    if (this.selectedItems.length > 0) {
      this.scanCodes = this.scanCodes.filter(code => !this.selectedItems.some(selected => selected.code === code.code));
      this.processScanCodes();
      this.removeItemBtnEnabled = false;
    }
  }

  toggleScanBarcode(): void {
    this.isCameraFullScreen = !this.isCameraFullScreen;
    if (!this.scanner) {
      console.error('Scanner is not initialized');
      return;
    }

    this.scanner.maxWidth = this.isCameraFullScreen ? '1000' : '640';
    this.scanner.maxHeight = this.isCameraFullScreen ? '1000' : '640';
    this.ShowScanBarcode = !this.ShowScanBarcode;
    if (this.ShowScanBarcode) {
      this.scanfirstime = true;
      if (!this.isScannerInitialized) {
        this.scanner.start();
        this.isScannerInitialized = true;
      }
    } else {
      this.scanner.stop();
      this.isScannerInitialized = false;
    }
  }

  closeCamera(): void {
    if (!this.scanner) {
      console.error('Scanner is not initialized');
      return;
    }

    this.scanner.stop();
    this.ShowScanBarcode = false;
    this.isScannerInitialized = false;
  }

  onValueChanges(result: any) {
    this.scanner.stop();
    this.isScannerInitialized = false;
    if (this.scanfirstime === false) {
      return;
    }
    this.scanfirstime = false;
    if (result && result.codeResult && result.codeResult.code) {
      this.barcode = result.codeResult.code;
      this.addScannedCode(this.barcode);
      this.ShowScanBarcode = false;
    }
  }

  addScannedCode(code: string) {
    if (!this.scanCodes.some(item => item.code === code)) {
      this.scanCodes.push({ code });
      this.processScanCodes();
    }
  }

  updateCameraZoom(): void {
    if (this.scannerElement) {
      const videoElement =
        this.scannerElement.nativeElement.querySelector('video');
      if (videoElement) {
        videoElement.style.transform = `scale(${this.cameraZoom})`;
        videoElement.style.transformOrigin = 'center center';
      }
    }
  }

  drawComplete(event: MouseEvent | Touch) {
    console.log('Completed drawing', event);
    console.log(this.signaturePad.save());
  }

  drawStart(event: MouseEvent | Touch) {
    console.log('Start drawing', event);
  }

  private initializeSignaturePad() {
    if (this.signaturePad) {
      this.signaturePad.clear();
    }
  }

  onSignatureChange() {
  }

  private dataURLToArray(dataURL: string): Uint8Array {
    const binaryString = atob(dataURL.split(',')[1]);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  private arrayBufferToBase64(buffer: Uint8Array): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  get displayPackageCount(): number | null {
    const cnt = this.packageCountModel?.packageCount ?? 0;
    return cnt === 0 ? null : cnt;
  }
  
  get displayManualCount(): number | null {
    const cnt = this.packageCountModel?.manualCount ?? 0;
    return cnt === 0 ? null : cnt;
  }

  onPopupHiding() {
    this.resetPackageEditPopupState();
  }

  private resetPackageEditPopupState() {
    this.packageCountModel = null; 
    this.scanCodes = [];
    this.packageEditFormPopupVisible = false; 
  }
}